/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

export const getServiceSessionCount = (service) => {
  const hasPricePerSession = Boolean(service.recurring_settings?.price_per_session);

  if (hasPricePerSession) {
    return service.recurring_settings?.repeat_count || 1;
  }

  return 1;
};

// Credits a service consumes: attendees (group) or sessions (recurring), else 1.
export const getServiceBookingUnits = (service) =>
  service.group_booking_count || service.recurring_settings?.repeat_count || 1;

export const getServicePrice = (service, fullPrice = false) =>
  fullPrice ? service.price : service.deposit_price ?? service.price;

export const getServiceTotalPrice = (service, fullPrice = false) => {
  const basePrice = getServicePrice(service, fullPrice);

  if (service.isLineAmount) {
    return basePrice;
  }

  const hasPricePerSession = Boolean(service.recurring_settings?.price_per_session);
  const hasGroupBooking = Boolean(service.group_booking_count);

  if (hasGroupBooking) {
    return basePrice * (service.group_booking_count || 1);
  }

  return hasPricePerSession ? basePrice * getServiceSessionCount(service) : basePrice;
};

export const canCombineService = (service, { isMultiBookingEnabled, isGroupBookingEnabled }) =>
  service?.can_combine &&
  (service?.min_group_booking === 1 || !isGroupBookingEnabled) &&
  isMultiBookingEnabled;

export const getCommonProviderIds = (services) => {
  if (!services?.length) return new Set();
  const [first, ...rest] = services.map((s) => new Set((s.providers || []).map(Number)));
  return rest.reduce((acc, set) => new Set([...acc].filter((id) => set.has(id))), first);
};

export const isProviderAvailableForService = (service, provider) =>
  !!provider &&
  (service.providers || []).some((id) => +id === +provider.id) &&
  service.min_group_booking <= provider.qty;

export const areServicesCombinable = (services, flags) => {
  if (services.length <= 1) return true;
  if (!flags.isMultiBookingEnabled) return false;
  if (!services.every((s) => canCombineService(s, flags))) return false;
  return getCommonProviderIds(services).size > 0;
};

export const isProviderCompatibleWithServices = (provider, services) => {
  if (!provider) return false;
  if (services.length === 0) return true;
  return services.every((s) => isProviderAvailableForService(s, provider));
};

const tagLineType = (items, isAttribute) =>
  (items ?? []).map((item) => ({
    ...item,
    isAttribute: item.product ? item.product.type === "attribute" : isAttribute,
  }));

const groupProductLines = (items, groupBookingCount = 0) => {
  const grouped = new Map();
  const occurrences = new Map();

  for (const item of items) {
    const key = item.product ? item.product.id : item.id;
    const qty = +item.qty || 1;
    const amount = item.product ? +item.amount || 0 : (+item.price || 0) * qty;

    occurrences.set(key, (occurrences.get(key) || 0) + 1);

    const existing = grouped.get(key);
    if (existing) {
      existing.qty += qty;
      existing.totalAmount += amount;
    } else {
      grouped.set(key, { ...item, qty, totalAmount: amount });
    }
  }

  return Array.from(grouped.entries()).map(([key, value]) => {
    if (value.isAttribute && !value.product && occurrences.get(key) === 1 && groupBookingCount > 0) {
      return { ...value, qty: groupBookingCount, totalAmount: (+value.price || 0) * groupBookingCount };
    }
    return value;
  });
};

export const mergeProductAndAddonLines = (addons, products, groupBookingCount = 0) =>
  groupProductLines([...tagLineType(addons, true), ...tagLineType(products, false)], groupBookingCount);
